// frontend/src/admin/EmailLog.jsx
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import {
  Alert, Box, Button, Skeleton, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, Typography, useMediaQuery, useTheme,
} from '@mui/material'
import Panel from '../components/common/Panel'
import ListToolbar from '../components/common/ListToolbar'
import FilterPillRow from '../components/common/FilterPillRow'
import TableViewport from '../components/common/TableViewport'
import TableFooterSimple from '../components/common/TableFooterSimple'
import MobileRecordCard from '../components/common/MobileRecordCard'
import EmptyState from '../components/common/EmptyState'
import Pill from '../components/common/Pill'
import RefreshButton from '../components/common/RefreshButton'
import useContainerWidth from '../components/common/useContainerWidth'
import useColumnPriority from '../components/common/useColumnPriority'
import useListQuery from '../components/common/listQuery/useListQuery'
import { COLUMN_MAX, CONTENT_WIDTH, FS, SP } from '../theme/tokens'
import { formatBerlinDateTime } from '../utils/time'

const API = process.env.REACT_APP_API_BASE_URL

/** 50 je Seite ist die Hausgroesse (§9.6) — die Seite blaettert am SERVER. */
const ROWS_PER_PAGE = 50

/** Die Servervorgabe des Zeitfensters (`backend/notify/admin_routes.py`). */
const SERVER_TAGE = 7

/**
 * Die vier Statuswerte der `CHECK`-Constraint (`migrations/088:76`) — genau die
 * Schluessel, die `counts` IMMER traegt (Plan §4.2(b), W-neu-5).
 */
const STATUSWERTE = [
  { value: 'sent', label: 'Sent' },
  { value: 'failed', label: 'Failed' },
  { value: 'bounced', label: 'Bounced' },
  { value: 'attempted', label: 'Attempted' },
]

/**
 * Die Zeitfenster des Blatts. 🔴 Der Etikett-Text ist der des CHIPS (§3.2
 * „Time: Last 30 days") — deshalb Objekte und nicht nackte Zahlen.
 */
const TAGE = [
  { value: '1', label: 'Last 24 hours' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
]

const tageLabel = (n) => (TAGE.find((t) => Number(t.value) === Number(n)) || {}).label
  || `Last ${n} days`

const KOPF_SX = { whiteSpace: 'nowrap' }

/** Feste Breiten der Kernspalten (Plan §2.10) — die Tabelle springt nicht.
 *
 *  🔴 **EINE Quelle fuer beide Zahlen** (R15-W2 der Runde 2): dieselbe Breite
 *  stand bis zur Nachbesserung 3 zweimal hier — als `COLUMN_PX[key]` fuer die
 *  Zelle und als `max` am Deskriptor fuer das Budget. Wer eine der beiden
 *  Stellen allein aendert, rechnet den Fit gegen eine Breite, die die Spalte
 *  nicht hat. Das `max` der Kernspalten wird deshalb aus dieser Tabelle
 *  abgeleitet (s. `SPALTEN`). */
const COLUMN_PX = {
  time: 150,
  recipient: 220,
  status: 110,
}

/**
 * Die Spalten der Tabelle — Deskriptoren fuer `useColumnPriority`.
 *
 * 🔴 **Reihenfolge = DOM-Reihenfolge** (§2.10): Time · Recipient · Subject ·
 * Transport · Status. Der Hook nimmt die OPTIONALEN Spalten in Rangfolge auf,
 * deshalb steht `subject` (`lg`) vor `transport` (`xl`).
 *
 * 🔴 **Budget aus den FESTEN Breiten, nicht aus den Art-Maxima** (R16-C2 der
 * Runde 1, Nachbesserung 2). Bis dahin rechnete der Hook mit
 * `COLUMN_MAX[kind].max` — 160 (date) + 360 (identity) + 200 (chip) = **720**,
 * + Subject 360 = 1080, + Transport 200 = **1280**. Recipient steht aber fest auf
 * 220, nicht 360: bei einem Behaelter von rund 1000–1100 px (Foto 1440) fiel
 * Transport an der Summe 1280, obwohl real ~324 px frei waren. Der Deskriptor traegt deshalb sein
 * eigenes `max` (additiv im Hook, R15-W3), und das Budget ist:
 *
 *   `kern` = 150 (time) + 220 (recipient) + 110 (status) = **480**
 *   + `subject` (`text`, 320) = **800**
 *   + `transport` (`chip`, 200) = **1000**
 *
 * 🔴 **Der Fit allein genuegt NICHT, und das ist die Lehre der Runde 2.** Der
 * Hook nimmt die Spalte, die den Behaelter UEBERSCHREITET, noch mit („die Tabelle
 * deckelt danach auf den Behaelter", `waehleSichtbare`); damit stand bei einem
 * 488er Behaelter Time · Recipient · Subject im DOM und **Status** — eine
 * `kern`-Spalte — lag hinter dem rechten Rand. `waehlePassendeSpalten` nimmt
 * darum so lange optionale Spalten von HINTEN wieder heraus, bis die
 * **Mindest**summe in den Behaelter passt.
 *
 * Der Behaelter ist NICHT `Viewport − Sidebar`: er ist
 * `Viewport − Sidebar − Seitenrand − 2×Panel-Polster − 2×Box-Polster`. Er wird
 * deshalb nicht mehr als feste Zahl notiert — die Fotos der Runde 2 hatten
 * 612/992/1152 notiert, waehrend bei 900 gemessene **488** im Behaelter lagen
 * (Panel 568 − 2×`SP.md` − 2×`SP.card`). Die Breiten misst der Orchestrator im
 * Browser (`R16-C2` in `frontend/tests/listen/admin-email-log.spec.ts`).
 */
const SPALTEN = [
  { key: 'time', priority: 'kern', kind: 'date', max: COLUMN_PX.time },
  { key: 'recipient', priority: 'kern', kind: 'identity', max: COLUMN_PX.recipient },
  { key: 'subject', priority: 'lg', kind: 'text' },
  { key: 'transport', priority: 'xl', kind: 'chip' },
  { key: 'status', priority: 'kern', kind: 'chip', max: COLUMN_PX.status },
]

function spaltenBreite(key) {
  const px = COLUMN_PX[key]
  return px ? { width: px, minWidth: px } : {}
}

/** Die Mindestbreite einer Spalte: feste Breite, sonst die Untergrenze ihrer Art. */
function spaltenMindestbreite(sp) {
  return COLUMN_PX[sp.key] ?? COLUMN_MAX[sp.kind]?.min ?? 0
}

/**
 * WaehlePassendeSpalten — nimmt optionale Spalten von HINTEN heraus, bis die
 * **Mindest**summe in den Behaelter passt (R16-C1 der Runde 2).
 *
 * 🔴 **Warum das noetig ist, obwohl der Hook schon fit.** `waehleSichtbare`
 * nimmt die Spalte, die den Behaelter UEBERSCHREITET, bewusst noch mit („die
 * Tabelle deckelt danach auf den Behaelter"). Fuer eine Tabelle, die den
 * Ueberschuss in breitere Zellen gibt, ist das richtig — fuer eine Tabelle mit
 * FESTEN Breiten und `tableLayout: 'fixed'` ist es falsch: bei 900 standen Time ·
 * Recipient · Subject im DOM und die Tabelle rollte, **Status** — eine
 * `kern`-Spalte — lag hinter dem rechten Rand. Wer dort eine fehlgeschlagene
 * Mail sucht, sah sie nicht.
 *
 * 🔴 **Von hinten heisst in ANZEIGEordnung**, nicht in Rangfolge: Transport
 * (zuletzt unter den optionalen) faellt vor Subject. `kern` faellt NIE — passt
 * die Mindestsumme der Kernspalten nicht, rollt die Tabelle weiterhin, und das
 * ist die einzige verbleibende Lage (der Behaelter ist dann schmaler als 480).
 *
 * 🔴 **Ohne gemessenen Behaelter wird nichts entfernt** (`null`, `undefined`,
 * `NaN`, `0` oder negativ): vor der ersten Messung ist die Menge die des
 * Musters, und ein Entfernen waere eine Behauptung ueber eine Breite, die noch
 * niemand kennt. Dieselbe Grenze wie im Hook (`Number.isFinite`); die 0 faellt
 * zusaetzlich heraus, weil `useContainerWidth` eine gemeldete 0 verwirft — sie
 * kaeme hier nur als ausdrueckliche 0 an, und die ist „nicht gemessen".
 *
 * Rein und benannt exportiert: Jest prueft die Auswahl ohne Layout (jsdom hat
 * keines), und die Ansicht ruft dieselbe Funktion.
 *
 * @param {Array<object>} spalten die Spalten in ANZEIGEordnung
 * @param {number|null} breite die gemessene Behaelterbreite
 * @returns {Array<object>} die verbleibenden Spalten, Eingabereihenfolge
 */
export function waehlePassendeSpalten(spalten, breite) {
  if (!Number.isFinite(breite) || breite <= 0) return spalten
  let gewaehlt = spalten.slice()
  const mindestsumme = () => gewaehlt.reduce((summe, sp) => summe + spaltenMindestbreite(sp), 0)
  while (mindestsumme() > breite) {
    let letzte = -1
    for (let i = gewaehlt.length - 1; i >= 0; i -= 1) {
      if (gewaehlt[i].priority !== 'kern') {
        letzte = i
        break
      }
    }
    if (letzte < 0) break
    gewaehlt = gewaehlt.filter((_, i) => i !== letzte)
  }
  return gewaehlt
}

/**
 * Text und Ton der Statuspille — EINE Quelle fuer Tabelle und reduzierte Zeile
 * (Regel 22 Nr. 2: dieselbe Darstellung ist eine Hilfsfunktion, kein zweiter
 * Ternaer). Der Status steht nie ueber Farbe allein (P22): er traegt Text.
 */
function statusPille(status) {
  const label = (STATUSWERTE.find((s) => s.value === status) || {}).label || status
  if (status === 'sent') return { text: label, tone: 'ok' }
  if (status === 'failed' || status === 'bounced') return { text: label, tone: 'err' }
  return { text: label, tone: 'plain' }
}

/** Die Statuspille — Tabelle und reduzierte Zeile teilen sie (eine Quelle). */
function StatusPille({ status }) {
  const p = statusPille(status)
  return <Pill tone={p.tone}>{p.text}</Pill>
}

/**
 * EmailLog — /admin/email-log page.
 *
 * @description
 *   Das E-Mail-Protokoll der Benachrichtigungen mit der Listenanatomie aus
 *   `tabellen-filter-suche.md` §2.1: Suche und Zaehler in Z1, vier
 *   Status-Quick-Filter mit Zaehlern in Z2, die aktiven Filter in Z3, das
 *   Zeitfenster im Blatt, eine Tabelle am Desktop und reduzierte Zeilen auf dem
 *   Telefon.
 *
 *   🔴 **Der Abruf laeuft am SERVER.** Suche (`recipient`), Status, `days` und
 *   die Seite (`limit`/`offset`) sind Serverparameter; `total` und `counts`
 *   kommen aus derselben Antwort. Vor diesem Bau filterte die Ansicht die
 *   geladene Seite clientseitig — ein Zaehler ueber eine gekappte Seite ist
 *   falsch (§4.2).
 *
 *   🔴 **`days` hat KEINE View-Vorgabe** (D-1): fehlt der Parameter, gilt die
 *   Server-Vorgabe von 7 Tagen. Der Chip in Z3 entsteht erst, wenn der Nutzer
 *   waehlt. **Das wirksame Fenster ist trotzdem sichtbar** — als Zeile
 *   „Showing emails from the last 7 days." ueber der Tabelle (`fenster-hinweis`)
 *   und im Blatt ueber `emptyLabel` (das rendert IM Blatt, nicht davor).
 *
 *   🔴 **Eine Pille mit Zaehler 0 bleibt bedienbar** (W11): die 0 ist eine
 *   GEMESSENE Aussage ueber den Bestand (M4: in 30 Tagen nur `sent`), kein
 *   „unbekannt". Ein Klick setzt den Filter und fuehrt in `no-match` mit
 *   „Clear filter".
 *
 *   🔴 **Unbekannt ist nie 0** (Regel 22 Nr. 3): scheitert ein Nachladen,
 *   waehrend die Liste leer steht, traegt der Zaehler „—" (`unknown`) — nicht
 *   die Zahl des letzten Erfolgslaufs.
 *
 * @useWhen
 *   - Nachsehen, welche Benachrichtigung an welche Adresse ging
 *   - Fehlgeschlagene oder zurueckgewiesene Mails eingrenzen
 *
 * @related
 *   - CronFailures — dieselbe Listenanatomie fuer fehlgeschlagene Cron-Laeufe
 *   - JobsList — Kartenliste derselben Konsole
 */
export default function EmailLog() {
  const theme = useTheme()
  const istSchmal = useMediaQuery(theme.breakpoints.down('sm'))

  // ── Der Listen-Zustand lebt in der ADRESSE (§3.1) ─────────────────────────
  const dimensionen = useMemo(() => [
    {
      key: 'status',
      kind: 'quick',
      label: 'Status',
      values: STATUSWERTE.map((s) => ({ value: s.value, label: s.label })),
    },
    { key: 'days', kind: 'single', label: 'Time', values: TAGE },
    { key: 'q', kind: 'single', label: 'Recipient' },
  ], [])
  const query = useListQuery({ dimensions: dimensionen, pageSize: ROWS_PER_PAGE })
  const { state, set, clearAll, chips } = query
  const gewaehlt = Array.isArray(state.status) ? state.status : []
  // Der Abruf haengt an einem PRIMITIVEN Schluessel: der Hook gibt bei jedem
  // Render ein neues Array zurueck, ein Array in der Dep-Liste liefe in eine
  // Endlosschleife.
  const statusSchluessel = gewaehlt.join(',')
  const seite = Math.max(1, Number(state.page) || 1)

  // 🔴 Das WIRKSAME Zeitfenster (R16-C1/R15-W1, Nachbesserung 2): ohne Nutzerwahl
  // ist es die SERVER-Vorgabe. Der Satz darunter steht sichtbar in der Grundansicht
  // („Showing emails from the last 7 days.") — bis dahin nannte es nur das Blatt,
  // und ein leerer Tag las sich als „gar keine Mails".
  const fensterTage = Number(state.days) || SERVER_TAGE
  const fensterLabel = tageLabel(fensterTage)
  const fensterSatz = `No emails in the ${fensterLabel.toLowerCase()}.`

  // ── Der Abruf ─────────────────────────────────────────────────────────────
  const [emails, setEmails] = useState([])
  const [gesamt, setGesamt] = useState(null)
  const [zaehler, setZaehler] = useState(null)
  const [laedt, setLaedt] = useState(true)
  const [fehler, setFehler] = useState(null)
  const [jeGeladen, setJeGeladen] = useState(false)
  const jeGeladenRef = useRef(false)
  const [versuch, setVersuch] = useState(0)

  useEffect(() => {
    const steuerung = new AbortController()
    let abgemeldet = false
    setLaedt(true)
    if (jeGeladenRef.current) setFehler(null)
    axios.get(`${API}/api/admin/notify/email-log`, {
      signal: steuerung.signal,
      params: {
        // 🔴 `days` wird IMMER gesendet (die wirksame Servervorgabe, wenn der
        // Nutzer nichts gewaehlt hat): `counts` und `total` gelten fuer DIESES
        // Fenster, und das Blatt nennt dasselbe.
        days: Number(state.days) || SERVER_TAGE,
        limit: ROWS_PER_PAGE,
        offset: (seite - 1) * ROWS_PER_PAGE,
        ...(statusSchluessel ? { status: statusSchluessel } : {}),
        ...((state.q || '').trim() ? { recipient: state.q.trim() } : {}),
      },
    })
      .then((r) => {
        if (abgemeldet) return
        const zeilen = Array.isArray(r.data?.emails) ? r.data.emails : []
        const zahl = Number.isFinite(r.data?.total) ? r.data.total : null
        setEmails(zeilen)
        setGesamt(zahl)
        setZaehler(r.data?.counts && typeof r.data.counts === 'object' ? r.data.counts : null)
        setFehler(null)
        setJeGeladen(true)
        jeGeladenRef.current = true
        // 🔴 Eine Seite hinter dem Ende wird auf die LETZTE geklemmt (N10.4):
        // die Ansicht schreibt die Adresse nicht um, sie zeigt den letzten
        // Stand. Ohne die Klemmung stuende „0 of N" ueber einer leeren Seite.
        if (zahl != null && zahl > 0) {
          const letzte = Math.max(1, Math.ceil(zahl / ROWS_PER_PAGE))
          if (seite > letzte) set('page', letzte)
        }
      })
      .catch((e) => {
        if (abgemeldet || steuerung.signal.aborted) return
        setFehler(e?.response?.data?.error || e?.message || 'Unknown error')
      })
      .finally(() => { if (!abgemeldet) setLaedt(false) })
    return () => { abgemeldet = true; steuerung.abort() }
  }, [statusSchluessel, state.days, state.q, seite, versuch, set])

  const wiederholen = useCallback(() => setVersuch((v) => v + 1), [])

  // 🔴 Die Sperre des ERSTEN Aufbaus (N8.5): nur solange noch nie etwas geladen
  // wurde. Ein spaeter gescheitertes Auffrischen laesst die Zeilen stehen.
  const sperre = !jeGeladen && fehler != null

  /**
   * Der Leerzustand.
   *
   * 🔴 `unknown` (R15-W1 der Schwester): ein gescheitertes NACHLADEN bei leerer
   * Liste kennt den Grund nicht — weder „nichts da" noch „nichts gefunden".
   *
   * 🔴 **`state.days` ist ein Filter wie jeder andere** (R16-C1/R15-W1). Stand
   * `days` nicht im Praedikat, zeigte ein gewaehltes, aber leeres Fenster
   * `no-data` — „No emails recorded." —, waehrend oben der Chip „Time: Last 24
   * hours" stand. Die einzige Dimension dieser Ansicht, die aus dem Raster fiel.
   */
  const leerGrund = sperre
    ? 'error'
    : jeGeladen && fehler != null && emails.length === 0
      ? 'unknown'
      : jeGeladen && emails.length === 0
        ? ((state.q || '').trim() || gewaehlt.length > 0 || Boolean(state.days)
          ? 'no-match'
          : 'no-data')
        : null

  const standUnbekannt = leerGrund === 'unknown'
  const hatSuche = Boolean((state.q || '').trim())

  // ── Breite und Spaltenbudget ─────────────────────────────────────────────
  const tabelleRef = useRef(null)
  const { width: behaelterBreite } = useContainerWidth(tabelleRef)
  const messBasis = behaelterBreite ?? CONTENT_WIDTH.reading
  const { visible: sichtbareSpalten } = useColumnPriority({
    columns: SPALTEN,
    containerWidth: messBasis,
  })
  // 🔴 R16-C1 der Runde 2: der Hook nimmt die ueberschreitende Spalte noch mit.
  // Bei 900 rollte die Tabelle deshalb, und Status lag hinter dem Rand. Der
  // Filter arbeitet auf der GEMESSENEN Breite (nicht auf `messBasis`): vor der
  // ersten Messung ist die Spaltenmenge die des Musters, und sie zu beschneiden
  // waere eine Behauptung ueber eine unbekannte Breite.
  const passendeSpalten = waehlePassendeSpalten(sichtbareSpalten, behaelterBreite)
  const sichtbar = new Set(passendeSpalten.map((s) => s.key))
  const spaltenMinSumme = passendeSpalten.reduce(
    (summe, sp) => summe + spaltenMindestbreite(sp),
    0,
  )

  // ── Die Quick-Filter-Reihe (Z2) — Zaehler vom Server, 0 bleibt bedienbar ──
  const quickReihe = (
    <Box>
      <FilterPillRow
        alwaysShow
        multi
        label="Status"
        options={STATUSWERTE.map((s) => ({
          ...s,
          // 🔴 Der Zaehler fehlt, solange nichts geladen ist — `undefined` zeichnet
          // keine Zahl. Eine 0 waere hier eine Behauptung ueber einen unbekannten
          // Bestand (Regel 22 Nr. 3).
          count: zaehler ? zaehler[s.value] : undefined,
        }))}
        value={gewaehlt}
        onChange={(v) => set('status', v)}
        // N8.5: gesperrt nur beim gescheiterten ERSTEN Aufbau. Eine Pille mit
        // Zaehler 0 ist NICHT gesperrt (W11).
        disabled={sperre}
      />
    </Box>
  )

  // ── Das Blatt (BS6, `variant="popover"`) — Zeitfenster, ohne View-Vorgabe ──
  const blatt = {
    criteria: [{
      key: 'days',
      label: 'Time',
      value: state.days ?? null,
      onChange: (v) => set('days', v || null),
      options: TAGE,
      // 🔴 `emptyLabel` nennt den wirksamen Wert, wenn das Kriterium keinen hat —
      // und rendert IM BLATT (`FilterSheet.jsx:944/959`), nicht davor. Hier stand
      // bis zur Nachbesserung 2 „jeder Kriterienwert ist OHNE Oeffnen lesbar" mit
      // Berufung auf N11.14/P17; das war falsch. Ohne Nutzerwahl ist der Wert die
      // SERVER-Vorgabe (D-1), und sichtbar in der GRUNDANSICHT ist er ueber die
      // Zeile „Showing emails from the last …" (`fensterHinweis` unten).
      emptyLabel: fensterLabel,
    }],
    // 🔴 P17 verlangt die klebende Fussleiste mit „Show N results" und
    // „Clear all". `null` (unbekannt) zeichnet „Show results" ohne Zahl — das
    // ist die ehrliche Form, solange nichts geladen ist.
    resultCount: Number.isFinite(gesamt) ? gesamt : null,
    onClearAll: clearAll,
    // N4.6: die Zahl im Knopf zaehlt die Blatt-Kriterien MIT Wert — die
    // Server-Vorgabe des Fensters ist kein gesetzter Filter.
    activeCount: state.days ? 1 : 0,
  }

  const leerzustand = (grund) => {
    if (grund === 'error') {
      return (
        <EmptyState
          reason="error"
          message="Emails could not be loaded."
          detail={fehler ? `Last error: ${fehler}` : undefined}
          onRetry={wiederholen}
          showDuringOutage
        />
      )
    }
    if (grund === 'unknown') {
      return (
        <EmptyState
          reason="unknown"
          message="The list could not be refreshed. Whether there are emails is unknown."
          detail={fehler ? `Last error: ${fehler}` : undefined}
          onRetry={wiederholen}
        />
      )
    }
    if (grund === 'no-match') {
      return (
        <EmptyState
          reason="no-match"
          // 🔴 W-neu-7: ein Klick auf eine 0-Pille setzt `status`, nicht `q` —
          // „Clear search" raeumt ihn nicht ab. Deshalb stehen BEIDE Wege: das
          // Echo bei Suche, sonst der Satz, und immer der Filter-Knopf, wenn
          // ein Filter gesetzt ist.
          message={hatSuche
            ? undefined
            // 🔴 Ohne Suchbegriff nennt der Satz den GRUND: der Statusfilter, sonst
            // das Zeitfenster (`fensterSatz`). „No emails match the current filter."
            // stand hier auch bei einem leeren Fenster — und behauptete damit einen
            // Filter, den der Bediener nicht gesetzt hatte.
            : (gewaehlt.length > 0 ? 'No emails match the current filter.' : fensterSatz)}
          noun={hatSuche ? 'email' : undefined}
          query={hatSuche ? state.q : undefined}
          onClearSearch={hatSuche ? () => set('q', null) : undefined}
          action={gewaehlt.length > 0 || state.days
            ? <Button variant="outlined" onClick={clearAll}>Clear filter</Button>
            : undefined}
        />
      )
    }
    return (
      <EmptyState
        reason="no-data"
        // 🔴 Das Fenster steht IM SATZ (R16-C1): „No emails recorded." war eine
        // Aussage ueber den BESTAND, obwohl nur die letzten 7 Tage gezaehlt sind.
        message={fensterSatz}
        detail="Emails appear here as soon as a notification is sent."
      />
    )
  }

  return (
    <Stack spacing={SP.section}>
      {/* Ein gescheitertes NACH-Laden, waehrend Zeilen stehen: die einzige
          Meldung, die der Leerzustand nicht abdeckt (G13a-2: ein Ausfall, eine
          Meldung). */}
      {fehler && !sperre && emails.length > 0 && (
        <Alert severity="error" variant="outlined">
          The list could not be refreshed: {fehler}. The rows below are the last
          successful load.
        </Alert>
      )}

      <Panel pad="none" data-testid="liste-panel">
        <Box sx={{ px: SP.card, pt: SP.card }}>
          <ListToolbar
            search={{
              value: state.q ?? '',
              onChange: (v) => set('q', v),
              // 🔴 Puffermodus (R15-B2, Nachbesserung 2): `onCommit` laesst
              // `SearchTrigger` den Begriff erst nach 300 ms Ruhe oder Enter
              // uebergeben — OHNE ihn feuert `onChange` bei jedem Tastendruck,
              // und die Suche ist ein SERVER-Parameter (ein `COUNT(*)` + ein
              // `GROUP BY` je Zeichen). Hausform: `ListenMuster.jsx:570-571`
              // (beide Handler), `DocsIndex.jsx:125-126` (nur `onCommit`).
              // Im Puffermodus ruft der Baustein `onChange` NIE — eine Quelle
              // fuer den Suchbegriff, sonst schriebe die Adresse zweimal.
              onCommit: (v) => set('q', v),
              placeholder: 'Recipient',
              label: 'Search recipient',
              inputRole: 'searchbox',
              disabled: sperre,
              // N2.9/N7.3 (Hausform `DocsIndex.jsx:132`, `JobsList.jsx:266`):
              // die Suche fuellt den Rest von Z1 — ohne diese Zeile faellt sie
              // auf ihre Eigenbreite (220 px) zurueck und „Filters“ schwebt
              // mitten in der Zeile (P24 rot, gemessen am Foto `1440_a8_grund`).
              sx: { flex: 1, minWidth: 0 },
            }}
            quickFilters={quickReihe}
            moreFilters={blatt}
            // 🔴 Nur der Fehler hat keinen Zaehler (Hausform `ListenMuster.jsx`);
            // `no-data`/`no-match` behalten ihn.
            count={leerGrund === 'error' ? undefined : {
              noun: 'emails',
              n: standUnbekannt || !jeGeladen ? null : emails.length,
              m: standUnbekannt || !jeGeladen ? null : gesamt,
              unknown: standUnbekannt,
              loading: laedt,
            }}
            refresh={(
              <RefreshButton
                ariaLabel="Refresh emails"
                loading={laedt}
                firstLoad={!jeGeladen}
                onClick={wiederholen}
              />
            )}
            query={{ chips, clearAll }}
          >
            {/* 🔴 Das WIRKSAME Zeitfenster, sichtbar in der Grundansicht (R16-C1).
                Bis zur Nachbesserung 2 nannte es nur das Blatt (hinter „Filters"),
                und ein leerer Tag las sich als „gar keine Mails". Die Zeile steht
                in BEIDEN Breiten (Z1 traegt den Zaehler nur am Desktop, Z3 am
                Tablet) und beruehrt den Zaehlertext nicht — `ResultCount` kann das
                Fenster nicht tragen: `noun` haengt die Mehrzahl an der ENDUNG auf
                (`noun.slice(0, -1)`), aus „emails, last 7 days" wuerde „1 emails,
                last 7 day", und P8 verlangt die Form „N of M emails". */}
            {/* Beim gescheiterten ERSTEN Aufbau gibt es keine Zeilen — dann waere
                „Showing …" eine Aussage ueber etwas, das nicht steht.
                🔴 R16-W2/W3 der Runde 2: dasselbe gilt fuer drei weitere Lagen,
                in denen die Zeile nichts zeigt oder doppelt sagt —
                  (a) `no-data`: der Leerzustand nennt das Fenster SELBST
                      („No emails in the last 7 days."), ~30 px darunter stand
                      derselbe Satz ein zweites Mal;
                  (b) `unknown` (gescheitertes Nachladen bei leerer Liste): der
                      Zaehler steht auf „—", eine Fensterzeile darueber behauptet
                      einen Bestand;
                  (c) der ERSTE Ladezustand (Skelett): es gibt noch keine Zeilen.
                `no-match` BEHAELT sie — dort ist „nichts gefunden im Fenster X"
                die Aussage, und der Leerzustand sagt sie ohne Fenster.
                Einzug (R16-W1): auf dem Telefon buendig zum Zaehler (`px: 0`),
                am Desktop buendig zum Tabellenkopf (`px: SP.card`). */}
            {!sperre && leerGrund !== 'no-data' && leerGrund !== 'unknown'
              && !(laedt && !jeGeladen) && (
              <Typography
                data-testid="fenster-hinweis"
                component="p"
                sx={{
                  px: istSchmal ? 0 : SP.card,
                  pt: SP.tight,
                  fontSize: FS.xs,
                  color: 'text.secondary',
                }}
              >
                {`Showing emails from the ${fensterLabel.toLowerCase()}.`}
              </Typography>
            )}

            {query.ignoredValues.length > 0 && (
              <Alert severity="info" data-testid="ignoriert-hinweis" sx={{ mt: SP.tight }}>
                {`Ignored unknown filter value “${query.ignoredValues[0].value}”.`}
              </Alert>
            )}

            {laedt && !jeGeladen && (
              <Box data-testid="liste-skelett" sx={{ px: SP.card, py: SP.card }}>
                <Stack spacing={SP.tight}>
                  {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} variant="rounded" height={44} />)}
                </Stack>
              </Box>
            )}

            {!istSchmal && !(laedt && !jeGeladen) && (
              <Box
                ref={tabelleRef}
                data-testid="liste-tabelle"
                aria-busy={laedt ? 'true' : 'false'}
                sx={{ px: SP.card, pt: SP.card }}
              >
                <TableViewport
                  footerPadPx={0}
                  priorities={SPALTEN}
                  containerWidth={behaelterBreite}
                  edgeHint
                >
                  <Table size="small" sx={{ tableLayout: 'fixed', minWidth: spaltenMinSumme }}>
                    <TableHead>
                      <TableRow>
                        {sichtbar.has('time') && (
                          // 🔴 P18: GENAU EINE Spalte traegt `aria-sort`, benannt
                          // und nicht aus einer Bibliothek abgeleitet. Der Wert
                          // kommt aus der Serverreihenfolge
                          // (`attempted_at DESC, id DESC`).
                          <TableCell
                            aria-sort="descending"
                            sx={{ ...KOPF_SX, ...spaltenBreite('time') }}
                          >
                            Time
                          </TableCell>
                        )}
                        {sichtbar.has('recipient') && (
                          <TableCell sx={{ ...KOPF_SX, ...spaltenBreite('recipient') }}>Recipient</TableCell>
                        )}
                        {sichtbar.has('subject') && <TableCell sx={KOPF_SX}>Subject</TableCell>}
                        {sichtbar.has('transport') && <TableCell sx={KOPF_SX}>Transport</TableCell>}
                        {sichtbar.has('status') && (
                          <TableCell sx={{ ...KOPF_SX, ...spaltenBreite('status') }}>Status</TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {leerGrund ? (
                        <TableRow>
                          <TableCell colSpan={passendeSpalten.length}>
                            {leerzustand(leerGrund)}
                          </TableCell>
                        </TableRow>
                      ) : emails.map((e) => (
                        <TableRow key={e.id} hover>
                          {sichtbar.has('time') && (
                            <TableCell sx={{
                              fontFamily: 'SF Mono, Menlo, monospace',
                              fontSize: FS.xs,
                              whiteSpace: 'nowrap',
                            }}>
                              {formatBerlinDateTime(e.attempted_at)}
                            </TableCell>
                          )}
                          {sichtbar.has('recipient') && (
                            <TableCell sx={{ fontSize: FS.sm }}>
                              {/* 🔴 R16-W-A der Runde 3: die Zelle ist 220 px
                                  breit und kuerzt mit Ellipse — ohne `title`
                                  waere eine Adresse ueber ~26 Zeichen nirgends
                                  mehr vollstaendig lesbar. Dieselbe Hausform
                                  wie die Betreff-Zeile darunter. */}
                              <Box
                                component="span"
                                title={e.recipient_email}
                                sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                              >
                                {e.recipient_email}
                              </Box>
                              {/* 🔴 R16-C1 der Runde 2: faellt die Subject-Spalte
                                  (schmaler Behaelter), geht der Betreff NICHT
                                  verloren — er steht als zweite Zeile hier.
                                  Hausform: `RMAFaelligkeit.jsx` (Kennung als
                                  Link, `caption`-Zeile darunter) und
                                  `POInbox.jsx` (Name, E-Mail als zweite Zeile).
                                  Nur bei fehlender Spalte, nie doppelt.
                                  `title` traegt den vollen Text, weil die Zeile
                                  bei `tableLayout: 'fixed'` gekuerzt wird
                                  (Ellipse greift auf dem Block, dessen Breite
                                  die Zelle vorgibt). */}
                              {!sichtbar.has('subject') && (
                                <Box
                                  component="span"
                                  title={e.subject}
                                  data-testid="betreff-zeile"
                                  sx={{
                                    display: 'block',
                                    fontSize: FS.xs,
                                    color: 'text.secondary',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {e.subject}
                                </Box>
                              )}
                            </TableCell>
                          )}
                          {sichtbar.has('subject') && (
                            <TableCell sx={{ fontSize: FS.sm }}>{e.subject}</TableCell>
                          )}
                          {sichtbar.has('transport') && (
                            <TableCell sx={{ fontSize: FS.xs, color: 'text.secondary' }}>
                              {e.transport}
                            </TableCell>
                          )}
                          {sichtbar.has('status') && (
                            <TableCell><StatusPille status={e.status} /></TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableViewport>
              </Box>
            )}

            {istSchmal && !(laedt && !jeGeladen) && (
              <Box data-testid="liste-karte" sx={{ px: SP.card, pt: SP.card }}>
                {leerGrund ? (
                  <Box>{leerzustand(leerGrund)}</Box>
                ) : (
                  <Stack spacing={SP.tight}>
                    {emails.map((e) => (
                      <MobileRecordCard
                        key={e.id}
                        variant="row"
                        data-testid={`karte-kennung-${e.id}`}
                        // 🔴 N11.7: die reduzierte Zeile traegt JEDES Feld der
                        // Desktop-Zeile (P15) — Empfaenger (Titel), Zeit
                        // (Kennung), Betreff und Transport (meta), Status (chip).
                        title={(
                          <Typography sx={{ fontSize: FS.sm, fontWeight: 600 }}>
                            {e.recipient_email}
                          </Typography>
                        )}
                        id={formatBerlinDateTime(e.attempted_at)}
                        meta={(
                          <Stack spacing={SP.chip}>
                            <Typography sx={{ fontSize: FS.sm }}>{e.subject}</Typography>
                            <Typography sx={{ fontSize: FS.xs, color: 'text.secondary' }}>
                              {e.transport}
                            </Typography>
                          </Stack>
                        )}
                        chips={<StatusPille status={e.status} />}
                        fieldTestIds={{
                          title: `karte-titel-${e.id}`,
                          id: `karte-zeit-${e.id}`,
                          meta: `karte-meta-${e.id}`,
                          chips: `karte-status-${e.id}`,
                        }}
                      />
                    ))}
                  </Stack>
                )}
              </Box>
            )}

            {/* Die Fusszeile steht NUR mit mehr als einer Seite (N8.7). */}
            {(gesamt ?? 0) > ROWS_PER_PAGE && !leerGrund && (
              <Box data-testid="liste-fuss">
                <TableFooterSimple
                  count={gesamt ?? 0}
                  page={seite - 1}
                  onPageChange={(p) => set('page', p + 1)}
                  rowsPerPage={ROWS_PER_PAGE}
                  // BS17 (das EINE Opt-in dieser Karte): mobil steht die Leiste
                  // im Fluss nach der letzten Karte.
                  stickyOnMobile={false}
                />
              </Box>
            )}
          </ListToolbar>
        </Box>
      </Panel>
    </Stack>
  )
}
